/**
 * formatDuration.js — Source unique du formatage de durée (UI humain)
 *
 * Helper aligné strictement avec le mobile (sprint v0.7.1.D-fix-4,
 * commit 0ba16d1e du repo mobile). La source de vérité pour l'affichage
 * humain est `end_time - start_time` (millisecondes), PAS le champ
 * `duration_min` du modèle Session — ce dernier est arrondi à la minute
 * au moment de l'enregistrement et reste réservé aux stats/agrégats backend.
 *
 * Format produit :
 *   - 0 – 59 s              → « X s »
 *   - 1 min – 59 min 59 s   → « X min Y s » (la part secondes n'est jamais omise)
 *   - 1 h – 23 h 59 min     → « X h Y min » (la part secondes est omise)
 *   - ≥ 24 h                → « Jj Hh Mmin » (style collé, ex. « 6j 23h 56min » ; secondes omises)
 *   - invalid (NaN, négatif, null, undefined, non-number) → « — »
 *
 * Palier jours (≥ 24 h) : ne concerne en pratique que le web (sessions
 * EkynAir Live multi-jours). Côté mobile (capteur portable, autonomie < 24 h)
 * une session ne franchit jamais 24 h ; sous 24 h web et mobile produisent le
 * même rendu, au-dessus seul le web est concerné → aucune divergence réelle.
 * Restauré au sprint v10.5.154 (cf. D-DURATION-1) ; ce palier avait été retiré
 * par erreur le 2026-05-08 (commit b27e6de) sous une prémisse d'alignement
 * mobile infondée.
 *
 * @param {number} durationMs - Durée en millisecondes
 * @returns {string} Durée formatée
 */
export function formatHumanDuration(durationMs) {
  if (
    durationMs === null ||
    durationMs === undefined ||
    typeof durationMs !== 'number' ||
    !Number.isFinite(durationMs) ||
    durationMs < 0
  ) {
    return '—';
  }

  const totalSeconds = Math.floor(durationMs / 1000);

  if (totalSeconds < 60) {
    return `${totalSeconds} s`;
  }

  const totalMinutes = Math.floor(totalSeconds / 60);

  if (totalMinutes < 60) {
    const seconds = totalSeconds % 60;
    return `${totalMinutes} min ${seconds} s`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (totalHours < 24) {
    return `${totalHours} h ${minutes} min`;
  }

  // ≥ 24 h — palier jours (style collé « Jj Hh Mmin »), sessions web multi-jours.
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `${days}j ${hours}h ${minutes}min`;
}
